import type { Article, ArticleSource, ReviewRecord } from "../shared";

/**
 * The reading surface, as checked-in typed content.
 *
 * These are written the way the curriculum is written: typed records rather
 * than Markdown files, validated at the same gate, and preview-only until a
 * human has read them. `npm run content:validate:publish` refuses to deploy
 * anything on this page that still says "machine-reviewed".
 *
 * The editorial shape is not enforced by any of that, and it is what decides
 * whether a paragraph survives being lifted out of the page by an answering
 * model:
 *
 * - the lede answers the title in plain sentences, before context and before
 *   the product, so a quote of the opening alone is still correct;
 * - every paragraph repeats its subject instead of opening with "this", so a
 *   block read on its own still says what it is about;
 * - headings are phrased as the question a reader typed, because a heading is
 *   a retrieval anchor before it is a layout device;
 * - a mapping from situation to consequence is a table, which is the most
 *   reliably extracted structure on a page;
 * - a factual claim carries a date and an authority, because an undated claim
 *   ages into a wrong one without anyone noticing.
 */

const REVIEW: ReviewRecord = {
  status: "machine-reviewed",
  reviewedBy: "sprinkledays-editorial",
  reviewedAt: "2026-08-16",
  source:
    "Drafted from the SprinkleDays product model and the sources cited on each article.",
};

const SOURCES: Record<string, ArticleSource> = {
  retrievalPractice: {
    id: "learning-scientists-retrieval-practice",
    authority: "The Learning Scientists",
    title: "Retrieval Practice",
    url: "https://www.learningscientists.org/retrieval-practice",
    lastChecked: "2026-08-16",
    nextReview: "2027-02-16",
  },
  spacedPractice: {
    id: "learning-scientists-spaced-practice",
    authority: "The Learning Scientists",
    title: "Spaced Practice",
    url: "https://www.learningscientists.org/spaced-practice",
    lastChecked: "2026-08-16",
    nextReview: "2027-02-16",
  },
  cefr: {
    id: "coe-cefr",
    authority: "Council of Europe",
    title: "Common European Framework of Reference for Languages (CEFR)",
    url: "https://www.coe.int/en/web/common-european-framework-reference-languages",
    lastChecked: "2026-08-16",
    nextReview: "2027-02-16",
  },
  speechSynthesis: {
    id: "mdn-speech-synthesis",
    authority: "MDN Web Docs",
    title: "SpeechSynthesis",
    url: "https://developer.mozilla.org/en-US/docs/Web/API/SpeechSynthesis",
    lastChecked: "2026-08-16",
    nextReview: "2027-02-16",
  },
  unicodeThai: {
    id: "unicode-thai-block",
    authority: "Unicode Consortium",
    title: "Thai, Range 0E00 to 0E7F",
    url: "https://www.unicode.org/charts/PDF/U0E00.pdf",
    lastChecked: "2026-08-16",
    nextReview: "2027-02-16",
  },
  omniglotThai: {
    id: "omniglot-thai",
    authority: "Omniglot",
    title: "Thai language, alphabet and pronunciation",
    url: "https://www.omniglot.com/writing/thai.htm",
    lastChecked: "2026-08-16",
    nextReview: "2027-02-16",
  },
  languageDeclarations: {
    id: "w3c-language-declarations",
    authority: "W3C Internationalization",
    title: "Declaring language in HTML",
    url: "https://www.w3.org/International/questions/qa-html-language-declarations",
    lastChecked: "2026-08-16",
    nextReview: "2027-02-16",
  },
  fsiTraining: {
    id: "fsi-foreign-language-training",
    authority: "U.S. Department of State, Foreign Service Institute",
    title: "Foreign Language Training",
    url: "https://www.state.gov/foreign-language-training/",
    lastChecked: "2026-08-16",
    nextReview: "2027-02-16",
  },
  commonWords: {
    id: "most-common-words-in-english",
    authority: "Wikipedia",
    title: "Most common words in English",
    url: "https://en.wikipedia.org/wiki/Most_common_words_in_English",
    lastChecked: "2026-08-16",
    nextReview: "2027-02-16",
  },
  dutchLanguageUnion: {
    id: "nederlandse-taalunie",
    authority: "Nederlandse Taalunie",
    title: "Taalunie, the Dutch Language Union",
    url: "https://taalunie.org/",
    lastChecked: "2026-08-16",
    nextReview: "2027-02-16",
  },
};

export const ARTICLES: Article[] = [
  {
    slug: "how-many-words-a-day-should-you-learn",
    title: "How many words a day should you learn?",
    description:
      "Five new words a day finishes a starter course in a month. How to choose a daily word count you can actually keep, and what changes when you raise it.",
    lede: "Five new words a day is the right number for most people starting a language, because it is small enough to survive a bad week. A daily word count is a pace control rather than an ambition: it decides how long a course lasts, how much you have to recall in one sitting, and whether you still open the sheet on the fourth Tuesday.",
    emoji: "🖐️",
    category: "Method",
    date: "2026-08-16",
    updated: "2026-08-16",
    primaryKeyword: "how many words a day to learn a language",
    secondaryKeywords: [
      "how many new words a day",
      "language learning pace",
      "words per day vocabulary",
      "daily vocabulary goal",
    ],
    tags: ["pace", "method", "vocabulary"],
    ctaLabel: "Set your pace and start",
    ctaHref: "/",
    related: [
      "the-fold-and-test-method",
      "printable-language-sheets-or-a-flashcard-app",
    ],
    author: null,
    noindex: false,
    publicationStatus: "preview",
    review: REVIEW,
    body: [
      {
        kind: "paragraph",
        text: "The number you pick is not a measure of how serious you are. It is the length of your course, decided in advance. A starter pool of 155 words is one month at five a day and two weeks at twelve, and the words themselves are identical either way.",
      },
      { kind: "heading", text: "What does a daily word count actually control?" },
      {
        kind: "paragraph",
        text: "A daily word count controls three things at once, and only one of them is obvious. It sets how long the course runs, how much sits on a single sheet, and how much you have to hold in your head between one sitting and the next. Raising it shortens the course and lengthens the sheet at the same time.",
      },
      {
        kind: "table",
        caption: "A 155-word starter pool at different paces",
        columns: ["Words a day", "Days to finish", "What one sheet feels like"],
        rows: [
          ["3", "52 days", "A minute of reading, easy to keep on a hard day"],
          ["5", "31 days", "The default: a month, and a sheet you can recall in one go"],
          ["8", "20 days", "Three weeks, and a sheet that needs two passes"],
          ["10", "16 days", "A fortnight, and topics start blurring together"],
          ["12", "13 days", "Two weeks, and most days cross a topic boundary"],
        ],
      },
      {
        kind: "paragraph",
        text: "Notice what does not change in that table. The pool is the same 155 words at every pace, in the same order, so day one opens on the same word whether you set three or twelve. Pace moves the window over the words; it does not choose different words.",
      },
      { kind: "heading", text: "Is five words a day too slow?" },
      {
        kind: "paragraph",
        text: "Five words a day is slow in the way a savings account is slow. It is 35 words in a week and about 1,800 in a year, which is well past the point where a language stops being a wall of noise. The failure mode of language learning is almost never a pace that was too gentle; it is a pace that was abandoned in week three.",
      },
      {
        kind: "paragraph",
        text: "A course you finish at three words a day teaches you more than a course you quit at fifteen. That sounds like consolation, but it is arithmetic: a quit course teaches you whatever you covered before you quit, and nothing after it.",
      },
      { kind: "heading", text: "What changes when you raise the count?" },
      {
        kind: "paragraph",
        text: "Raising the count changes the shape of a day, not just its size. At five words a sheet stays inside one topic, so the words on it support each other: the greetings sit together, the numbers sit together. At ten or twelve a day the window regularly crosses a topic boundary and pulls from two lessons at once, so a single sheet can ask you to hold food words and direction words in the same breath.",
      },
      {
        kind: "list",
        items: [
          "A wider day means more words competing for the same recall attempt.",
          "A wider day is more likely to span two topics, which removes the context that made the words easier.",
          "A wider day is a longer sitting, and a longer sitting is the first thing a busy evening cuts.",
        ],
      },
      {
        kind: "callout",
        title: "Raise the pace for a reason, not a mood",
        text: "A good reason to raise the count is that yesterday's words came back without effort three days running. A bad reason is that you have a free Sunday and feel behind.",
      },
      { kind: "heading", text: "How do you know your pace is wrong?" },
      {
        kind: "paragraph",
        text: "The signal that a pace is too high is not that the sheet is hard. It is that yesterday is gone. If you can fold today's sheet and recall today's words but cannot recall last Thursday's at all, the daily count is outrunning what is being kept, and the honest fix is to lower it.",
      },
      {
        kind: "table",
        caption: "What each signal actually means",
        columns: ["What you notice", "What it usually means", "What to change"],
        rows: [
          [
            "Today's words come back, last week's do not",
            "Too much new material per day",
            "Lower the word count by two or three",
          ],
          [
            "The sheet takes more than ten minutes",
            "The sitting is long enough to skip",
            "Lower the word count, or drop a sentence",
          ],
          [
            "Everything comes back instantly, three days running",
            "The pace is under what you can hold",
            "Raise the word count by one or two",
          ],
          [
            "You skipped four days and dread opening it",
            "The pace was never the problem",
            "Keep the pace, open today's sheet, skip the guilt",
          ],
        ],
      },
      { kind: "heading", text: "How do you change pace without losing your place?" },
      {
        kind: "paragraph",
        text: "Changing pace should re-cut the course, not restart it. Because the words are one ordered pool and the daily count is a window over it, moving from five to ten a day does not throw anything away: it re-slices the same words into fewer, wider days. Day one still opens on the same first word, and the course length in the header simply changes from 31 days to 16.",
      },
      {
        kind: "paragraph",
        text: "That is worth checking in whatever tool you use. If raising your pace resets your progress or shuffles the vocabulary, you will avoid changing it, and a pace you are afraid to change is a pace that is quietly wrong for months.",
      },
      { kind: "heading", text: "So what should you set on day one?" },
      {
        kind: "paragraph",
        text: "Set five, and change it after two weeks rather than after two days. Two weeks is long enough to include one bad evening, one busy weekend, and one day you nearly skipped, which is the only honest test of whether a pace fits an actual life. Then read [the fold-and-test method](/articles/the-fold-and-test-method) for how to spend those five minutes, because how you practise the words decides more than how many you take. If you want to see what a month at that pace actually buys, [the first 100 Thai words](/articles/first-100-thai-words) sets out the topics it covers and the day each one lands on.",
      },
    ],
    faqs: [
      {
        question: "How many words a day should a beginner learn?",
        answer:
          "Five a day is a good default for a beginner. It is small enough to keep on a bad day, it finishes a 155-word starter pool in about a month, and it leaves room for the sentences that make the words usable. Adjust after two weeks rather than after two days.",
      },
      {
        question: "Is it better to learn 5 words a day or 20 words a week?",
        answer:
          "Five a day beats twenty in one sitting, because the same material spread over more days is recalled better than the same material crammed into one. A daily rhythm also gives you six more chances to notice a word you have already forgotten.",
      },
      {
        question: "Can I learn a language with only 10 minutes a day?",
        answer:
          "Yes, for the vocabulary and phrases that make up the first months. Ten minutes a day is enough to learn five new words and test yourself on them. It is not enough to reach fluency on its own, because fluency needs listening and conversation as well as recall.",
      },
      {
        question: "Will raising my daily word count lose my progress?",
        answer:
          "It should not. A well-built course keeps the vocabulary in one ordered pool and treats the daily count as a window over it, so raising the pace re-cuts the same words into fewer, wider days. Day one still opens on the same word, and only the number of days changes.",
      },
      {
        question: "How many words do I need to hold a simple conversation?",
        answer:
          "A few hundred well-chosen words carry an enormous amount of ordinary conversation, because greetings, numbers, food, directions and politeness cover most everyday exchanges. The Council of Europe describes the levels this maps to, from A1 upward, in the Common European Framework of Reference.",
      },
    ],
    sources: [SOURCES.spacedPractice, SOURCES.retrievalPractice, SOURCES.cefr],
  },

  {
    slug: "printable-language-sheets-or-a-flashcard-app",
    title: "Printable language sheets or a flashcard app?",
    description:
      "Paper and flashcard apps solve different problems. When a printed language sheet beats an app for daily practice, and when it clearly does not.",
    lede: "A printable language sheet and a flashcard app are not competing versions of the same tool. A sheet is a fixed, finite day you can finish; an app is an endless queue that decides what you see. Paper wins when the problem is showing up at all, and loses when the problem is remembering something you met four months ago.",
    emoji: "🖨️",
    category: "Method",
    date: "2026-08-16",
    updated: "2026-08-16",
    primaryKeyword: "printable language learning sheets",
    secondaryKeywords: [
      "printable vocabulary worksheets",
      "paper flashcards vs app",
      "spaced repetition alternative",
      "language learning without an app",
    ],
    tags: ["method", "printing", "tools"],
    ctaLabel: "Print your first sheet",
    ctaHref: "/",
    related: [
      "how-many-words-a-day-should-you-learn",
      "the-fold-and-test-method",
    ],
    author: null,
    noindex: false,
    publicationStatus: "preview",
    review: REVIEW,
    body: [
      {
        kind: "paragraph",
        text: "The argument between paper and apps is usually had at the wrong level. Both present vocabulary, both can test recall, and both work. What differs is what happens on the day you do not feel like it, and that is the day the whole thing is decided.",
      },
      { kind: "heading", text: "What does a printed sheet do that an app does not?" },
      {
        kind: "paragraph",
        text: "A printed sheet has an end. Five words and two sentences on one page is a task with a visible edge: you can see the whole of today from the top of the page, and you know what finishing looks like before you start. A review queue has no such edge, which is why an app session ends when you get bored rather than when you are done.",
      },
      {
        kind: "paragraph",
        text: "A printed sheet also survives the phone. Picking up a phone to study puts the study two taps away from every notification you have, and the sheet on the kitchen table is not competing with anything. That is not a moral point about screen time; it is about how many decisions stand between you and the first word.",
      },
      {
        kind: "table",
        caption: "What each format is actually good at",
        columns: ["Situation", "Printed sheet", "Flashcard app"],
        rows: [
          ["Starting a habit from nothing", "Strong: a finite, visible day", "Weak: an endless queue"],
          ["Reviewing 2,000 words from last year", "Weak: no scheduling", "Strong: that is the whole point"],
          ["Learning with someone else", "Strong: fold it and swap", "Weak: one account, one screen"],
          ["Hearing how a word sounds", "Weak: paper is silent", "Strong: audio on every card"],
          ["Studying without the internet", "Strong: it is paper", "Depends on the app"],
          ["Knowing when you are finished", "Strong: the page ends", "Weak: the queue does not"],
        ],
      },
      { kind: "heading", text: "What does a flashcard app do better?" },
      {
        kind: "paragraph",
        text: "A flashcard app schedules. Spaced repetition works by showing a word again just as you are about to lose it, and stretching the gap each time you get it right. Paper cannot do that on its own, because paper does not know what you got wrong on Tuesday. Over a year and a few thousand words, that scheduling is worth more than any advantage paper has.",
      },
      {
        kind: "paragraph",
        text: "An app also carries sound. A written word in a language you cannot pronounce is half a word, and this matters more in some languages than others: a Thai word you have only read is a word you cannot say, because the tone is not in the transliteration you half-remember. Most modern browsers can speak text aloud with a device voice, which is why a sheet is worth reading on a phone at least once before it is printed.",
      },
      { kind: "heading", text: "So when should you print instead of installing?" },
      {
        kind: "paragraph",
        text: "Print when the honest problem is that you are not practising at all. In the first month the enemy is not forgetting, it is not starting, and a stack of seven sheets on the table on Sunday night removes every decision from the following week. Install a spaced-repetition app later, when you have a few hundred words worth scheduling and a habit sturdy enough to survive an endless queue.",
      },
      {
        kind: "list",
        ordered: true,
        items: [
          "Print a week at a time so the decision is made once, not seven times.",
          "Keep the finished sheets in a stack rather than binning them: the stack is the only progress bar paper has.",
          "Read a sheet on a phone once for the audio, then print it for the recall.",
          "Move to a scheduling app when you have more words than one week of sheets can revisit.",
        ],
      },
      {
        kind: "callout",
        title: "You are allowed to use both",
        text: "A printed sheet for today's new words and an app for last month's is not a contradiction. They are doing different jobs: one gets you to the table, the other decides what you see once you are there.",
      },
      { kind: "heading", text: "Does printing actually help you remember more?" },
      {
        kind: "paragraph",
        text: "Printing does not help you remember; testing does. The reason a two-column printed sheet beats a printed word list is that the fold hides one column and forces you to produce the answer instead of recognising it. That mechanism, retrieval practice, is one of the most consistently supported findings in learning research, and it is available on paper for the cost of a crease. [The fold-and-test method](/articles/the-fold-and-test-method) covers how to run it.",
      },
      {
        kind: "paragraph",
        text: "The other thing paper does for memory is spacing, and it does it by accident. A sheet a day is spaced by definition, because you cannot do Thursday on Monday. An app will let you do a month of reviews in one evening, and that evening will feel productive and teach you very little.",
      },
      { kind: "heading", text: "What should you look for in a printable sheet?" },
      {
        kind: "paragraph",
        text: "A printable sheet is worth printing if both columns are real translations. A row where one side says a word and the other side says a grammar explanation cannot be folded away and recalled, because nobody ever wants to say a grammar explanation. Look for two columns you could genuinely swap, a fold line down the middle, and a page that ends on one side of one sheet. [The first 100 Thai words](/articles/first-100-thai-words) prints one day of a good two-column sheet in full, so you can see what you are looking for.",
      },
    ],
    faqs: [
      {
        question: "Are printable language sheets better than a flashcard app?",
        answer:
          "They are better at different things. A printed sheet is better for starting a daily habit, because it has a visible end and does not live on your phone. A flashcard app is better for reviewing hundreds of older words, because it schedules them for you. Most people are best served by paper first and scheduling later.",
      },
      {
        question: "Do I need a printer to use printable language sheets?",
        answer:
          "No. A sheet designed for A4 reads perfectly well on a phone or tablet, and reading it on screen has one advantage paper does not: you can tap a word to hear it. Printing matters when you want the day to exist away from a screen, or when two people are sharing it.",
      },
      {
        question: "Is paper or an app better for spaced repetition?",
        answer:
          "An app, without much argument. Spaced repetition needs a record of what you got wrong and when, and paper has no memory. What paper does well is the daily spacing that comes free with one sheet a day, which is enough while your vocabulary is small.",
      },
      {
        question: "How often should I print new sheets?",
        answer:
          "A week at a time works best for most people. Printing seven days in one job turns a daily decision into a weekly one, and the finished stack gives you something a screen cannot: a visible record of the days you actually did.",
      },
    ],
    sources: [SOURCES.spacedPractice, SOURCES.retrievalPractice, SOURCES.speechSynthesis],
  },

  {
    slug: "the-fold-and-test-method",
    title: "The fold-and-test method: active recall on one sheet",
    description:
      "Folding a two-column sheet turns reading into recall. How the fold-and-test method works, why testing beats rereading, and how to run it in five minutes.",
    lede: "The fold-and-test method is a two-column sheet folded down the middle so one language is hidden, forcing you to produce the answer instead of reading it. It works because retrieving something from memory strengthens it far more than seeing it again does, and a crease is the cheapest way to make retrieval unavoidable.",
    emoji: "📄",
    category: "Method",
    date: "2026-08-16",
    updated: "2026-08-16",
    primaryKeyword: "active recall language learning",
    secondaryKeywords: [
      "fold and test method",
      "retrieval practice vocabulary",
      "self testing language",
      "two column vocabulary sheet",
    ],
    tags: ["method", "recall", "printing"],
    ctaLabel: "Make today's sheet",
    ctaHref: "/",
    related: ["how-many-words-a-day-should-you-learn", "learning-a-language-together"],
    author: null,
    noindex: false,
    publicationStatus: "preview",
    review: REVIEW,
    body: [
      {
        kind: "paragraph",
        text: "Most vocabulary study fails in the same quiet way. You read the list, every word looks familiar, you feel finished, and two days later none of it comes back. Familiarity is not memory, and reading a list is very good at producing familiarity.",
      },
      { kind: "heading", text: "How does the fold-and-test method work?" },
      {
        kind: "paragraph",
        text: "The fold-and-test method needs a sheet with two columns of the same rows: the language you are learning on one side, the language you already speak on the other. Fold the page along the line between them so one column disappears behind the other. Now read down the visible column and say the hidden answer out loud, before you unfold to check.",
      },
      {
        kind: "list",
        ordered: true,
        items: [
          "Read the whole sheet once, unfolded, with both columns visible.",
          "Fold so the language you are learning is hidden, and produce it from the language you already speak.",
          "Unfold and check. Mark nothing; just notice which rows were guesses.",
          "Fold the other way and go again, which is a different and harder task.",
          "Stop. Five words is a five-minute job, and stretching it does not help.",
        ],
      },
      {
        kind: "paragraph",
        text: "The two directions are not the same exercise, and the difference matters. Going from your own language to the new one is production, and it is what you need in a conversation. Going from the new language to your own is recognition, and it is what you need to understand a reply. Recognition is easier, so start there on a hard day.",
      },
      { kind: "heading", text: "Why does testing beat rereading?" },
      {
        kind: "paragraph",
        text: "Testing beats rereading because the act of retrieving a word changes how easily it is retrieved next time. Rereading strengthens your sense that you know the word; retrieval strengthens the ability to get at it. Learning research has separated these two for decades under the name retrieval practice, and the result holds across ages, subjects and formats.",
      },
      {
        kind: "paragraph",
        text: "There is a second effect that matters just as much on a bad day. A failed retrieval tells you the truth immediately. Rereading a list never tells you that you would have failed, so you keep spending time on words you already have and none on the three you do not.",
      },
      {
        kind: "table",
        caption: "What each way of studying actually trains",
        columns: ["What you do", "What it feels like", "What it trains"],
        rows: [
          ["Read the list twice", "Productive and easy", "Recognition, and confidence"],
          ["Cover one column and recall", "Uncomfortable and slow", "Retrieval, which is what you need"],
          ["Say the answer out loud", "Slightly silly", "Retrieval plus pronunciation"],
          ["Write the answer down", "Slowest of the four", "Retrieval plus spelling and script"],
        ],
      },
      { kind: "heading", text: "Why fold a sheet instead of using cards?" },
      {
        kind: "paragraph",
        text: "A fold keeps the day intact. Cards can be shuffled, dropped, half-finished and lost, and a stack of cards never tells you it is done. One folded sheet is a fixed set of rows in a fixed order, which makes today a thing you can finish rather than a pile you work at.",
      },
      {
        kind: "paragraph",
        text: "A fold is also reversible in a way a card is not. The same sheet tests both directions with one movement of your hand, and, if two of you are learning, the same sheet tests two people from opposite sides. [Learning a language together](/articles/learning-a-language-together) covers how that works in practice.",
      },
      {
        kind: "callout",
        title: "Say it out loud, even alone",
        text: "Silent recall skips the hardest part of a new language, which is making your mouth produce it. Saying the answer aloud costs nothing and catches the words you can recognise but cannot pronounce.",
      },
      { kind: "heading", text: "How long should one sheet take?" },
      {
        kind: "paragraph",
        text: "One sheet should take about five minutes, and if it takes fifteen the sheet is too big rather than you being too slow. A five-minute daily task survives a bad evening; a fifteen-minute one gets postponed to the weekend and then to never. If your sheet is running long, lower the word count rather than the frequency, as [choosing a daily word count](/articles/how-many-words-a-day-should-you-learn) explains. Producing the answer aloud matters most where a sound is the difficulty, which is why it is the core of [learning English as a Thai speaker](/articles/learning-english-as-a-thai-speaker) too.",
      },
      { kind: "heading", text: "What do you do with yesterday's sheets?" },
      {
        kind: "paragraph",
        text: "Keep yesterday's sheets in a stack and refold an old one whenever you have a spare minute. A sheet from nine days ago is the single most useful thing you can test yourself on, because it is exactly old enough to have started slipping. That is spacing done by hand, and it is the one thing paper does not give you automatically.",
      },
      {
        kind: "paragraph",
        text: "The stack has a second job that is not about memory at all. A visible pile of finished days is the only progress bar paper has, and on the evening you nearly skip, it is a better argument than any streak counter.",
      },
    ],
    faqs: [
      {
        question: "What is the fold-and-test method?",
        answer:
          "The fold-and-test method is a way of studying a two-column vocabulary sheet by folding it down the middle so one language is hidden. You read the visible column, say the hidden answer aloud, then unfold to check. The fold is what turns reading into recall.",
      },
      {
        question: "Why is active recall better than rereading a word list?",
        answer:
          "Rereading builds a feeling of familiarity, while recall builds the ability to produce the word when you need it. Retrieval also tells you immediately which words you do not have, so your time goes to the three you are missing instead of the twenty you already know.",
      },
      {
        question: "Should I test myself in both directions?",
        answer:
          "Yes, and they are different exercises. Going from your own language into the new one trains production, which is what speaking needs. Going the other way trains recognition, which is what understanding a reply needs. Recognition is easier, so use it on days when the sheet feels heavy.",
      },
      {
        question: "How long should a daily vocabulary sheet take?",
        answer:
          "About five minutes for five new words and a couple of sentences. If a sheet regularly takes fifteen minutes, the sheet is too large rather than you being too slow, and the fix is to lower the daily word count rather than to study less often.",
      },
      {
        question: "Do I need to write the answers down?",
        answer:
          "Not usually. Saying the answer out loud is enough for most vocabulary and is much faster. Writing helps when the language uses a script you are still learning to produce, because spelling a word by hand trains something that saying it does not.",
      },
    ],
    sources: [SOURCES.retrievalPractice, SOURCES.spacedPractice],
  },

  {
    slug: "learning-thai-first-30-days",
    title: "Learning Thai from scratch: what the first 30 days cover",
    description:
      "A month of Thai buys greetings, numbers, food and directions. What a first 30 days should cover, in what order, and what is better left until later.",
    lede: "The first 30 days of Thai should buy you the exchanges you will actually have: a greeting, a thank you, a number, an order, and a direction. Thai is friendly to beginners in its grammar, which has no verb conjugation and no plurals, and hard on beginners in its sounds, because tone changes meaning and the script does not use spaces between words.",
    emoji: "🌏",
    category: "Thai",
    date: "2026-08-16",
    updated: "2026-08-16",
    primaryKeyword: "learn Thai for beginners",
    secondaryKeywords: [
      "learn Thai in 30 days",
      "Thai for beginners plan",
      "basic Thai words and phrases",
      "how hard is Thai to learn",
    ],
    tags: ["thai", "beginner", "curriculum"],
    ctaLabel: "Start Thai on day 1",
    ctaHref: "/",
    related: [
      "thai-politeness-particles-khrap-and-kha",
      "first-100-thai-words",
      "how-long-does-it-take-to-learn-thai",
    ],
    author: null,
    noindex: false,
    publicationStatus: "preview",
    review: REVIEW,
    body: [
      {
        kind: "paragraph",
        text: "A first month of Thai is not a course in Thai. It is a decision about which 150 or so words you meet first, and that decision matters more than any method, because the wrong first 150 words leave you unable to buy lunch while knowing the word for government.",
      },
      { kind: "heading", text: "What makes Thai easy, and what makes it hard?" },
      {
        kind: "paragraph",
        text: "Thai grammar is genuinely kind to a beginner. Verbs do not conjugate, nouns have no plural form, there are no articles, and word order carries most of the work. A sentence you build out of dictionary forms is often simply correct, which is not true in Dutch, French or German.",
      },
      {
        kind: "paragraph",
        text: "Thai pronunciation is where the difficulty sits. Thai is tonal, so the same syllable said at a different pitch is a different word, and a learner who ignores tone is not speaking with an accent but saying something else. The script is an abugida of its own, written without spaces between words, which is why romanisation is a crutch worth using early and worth dropping.",
      },
      {
        kind: "table",
        caption: "Where a beginner's effort actually goes",
        columns: ["Part of the language", "Difficulty for a beginner", "When to spend time on it"],
        rows: [
          ["Grammar and word order", "Low", "As it comes up, in sentences"],
          ["Everyday vocabulary", "Moderate", "Every day, a few words at a time"],
          ["Tone", "High", "From the first week, out loud"],
          ["Reading the script", "High", "Month two, once you can say things"],
          ["Politeness particles", "Low to learn, easy to forget", "Week one, then permanently"],
        ],
      },
      { kind: "heading", text: "What should the first 30 days cover?" },
      {
        kind: "paragraph",
        text: "The first 30 days should follow the order of a day out rather than the order of a textbook. You greet someone before you thank them, you count before you shop, and you order food before you ask for directions to somewhere else. A course built in that order gives you a complete small exchange every few days.",
      },
      {
        kind: "table",
        caption: "A workable order for a first month",
        columns: ["Days", "Topic", "What it buys you"],
        rows: [
          ["1 to 4", "Greetings, thanks, yes and no", "A polite opening and a polite exit"],
          ["5 to 9", "Numbers and prices", "Markets, taxis, and understanding an answer"],
          ["10 to 14", "Food and drink, ordering", "The exchange you will have most often"],
          ["15 to 19", "Places and directions", "Getting somewhere and back"],
          ["20 to 24", "People, family, and introductions", "The second half of a first conversation"],
          ["25 to 30", "Time, days, and simple plans", "Arranging anything at all"],
        ],
      },
      {
        kind: "paragraph",
        text: "Five words a day across that plan is about 150 words in a month, which is enough for all six exchanges above at a beginner's level. If you want the arithmetic behind choosing that pace, [how many words a day to learn](/articles/how-many-words-a-day-should-you-learn) covers what changes when you raise it.",
      },
      { kind: "heading", text: "Should you learn the Thai script in the first month?" },
      {
        kind: "paragraph",
        text: "Learning the Thai script in month one is usually a mistake, and dropping it entirely is a bigger one. The script takes weeks of its own, and those weeks are better spent after you can already say a hundred things, because you will then be reading words you recognise rather than decoding shapes. Start the script in month two, and treat romanisation before that as scaffolding you intend to remove.",
      },
      {
        kind: "paragraph",
        text: "There is a real cost to staying on romanisation forever. Romanised Thai cannot represent tone consistently, several competing systems are in use, and none of them is what anyone writes on a menu or a street sign. Every month you delay the script is a month of building a habit you will have to undo.",
      },
      {
        kind: "callout",
        title: "Say every new word out loud on day one",
        text: "In a tonal language, a word you have only read silently is a word you have not learned. Reading a sheet on a phone once, tapping each Thai word to hear it, then printing it for recall is a good order.",
      },
      { kind: "heading", text: "What should you leave until later?" },
      {
        kind: "paragraph",
        text: "Leave classifiers, formal register, and the royal and monastic vocabulary until well past the first month. They are real Thai and you will eventually need some of them, but none of them appears in the exchanges a beginner actually has, and each of them costs the space of a week of useful words.",
      },
      {
        kind: "list",
        items: [
          "Classifiers: needed for counting things precisely, but a beginner is understood without them.",
          "Formal and written registers: different vocabulary for situations you are not in yet.",
          "Regional varieties such as Isan or Northern Thai: fascinating, and not what you are being answered in.",
          "The full tone rules for reading: they belong with the script, in month two.",
        ],
      },
      {
        kind: "paragraph",
        text: "One thing that looks skippable and is not: the politeness particles. They cost almost nothing to learn, they change with the speaker rather than the sentence, and leaving them off makes ordinary Thai sound blunt. [Thai politeness particles](/articles/thai-politeness-particles-khrap-and-kha) covers when to use each one.",
      },
    ],
    faqs: [
      {
        question: "How hard is Thai to learn for an English speaker?",
        answer:
          "Thai grammar is easier than most European languages, with no verb conjugation, no plurals and no articles. The difficulty is in pronunciation and reading, because Thai is tonal and its script is written without spaces between words. Expect the sounds to take longer than the grammar.",
      },
      {
        question: "What should I learn first in Thai?",
        answer:
          "Start with greetings, thank you, yes and no, and the politeness particle that matches your own gender. That handful covers the opening and closing of almost every exchange you will have, and it makes everything you say afterwards sound like an attempt rather than a demand.",
      },
      {
        question: "Should I learn the Thai alphabet before speaking?",
        answer:
          "No. Spend the first month on spoken words with romanisation as scaffolding, then start the script in month two. Learning the script after you can already say a hundred words means you are recognising words rather than decoding unfamiliar shapes.",
      },
      {
        question: "How many Thai words do I need for a holiday?",
        answer:
          "About 150 well-chosen words carry a two-week trip, if they are the right ones. Greetings, numbers, food, directions, and a few polite phrases cover the exchanges a visitor actually has, which is why a starter course should be ordered by situation rather than by grammar.",
      },
      {
        question: "Is romanised Thai good enough to learn from?",
        answer:
          "It is good enough to start with and not good enough to stay on. Romanisation cannot show tone reliably, several competing systems exist, and none of them appears on a menu or a road sign. Treat it as scaffolding you plan to remove in month two.",
      },
    ],
    sources: [SOURCES.omniglotThai, SOURCES.unicodeThai, SOURCES.cefr],
  },

  {
    slug: "thai-politeness-particles-khrap-and-kha",
    title: "Thai politeness particles: when to say ครับ and ค่ะ",
    description:
      "Thai politeness particles change with the speaker, not the sentence. When a man says khrap, when a woman says kha, and why kha shifts tone in a question.",
    lede: "In Thai, politeness is added at the end of a sentence with a particle that depends on who is speaking. A man says ครับ (khrap) and a woman says ค่ะ (kha), regardless of who they are talking to. The particle is not part of the sentence's meaning, which is exactly why it is so easy to leave off and so noticeable when you do.",
    emoji: "🙏",
    category: "Thai",
    date: "2026-08-16",
    updated: "2026-08-16",
    primaryKeyword: "thai politeness particles",
    secondaryKeywords: [
      "khrap kha meaning",
      "when to say khrap",
      "polite Thai phrases",
      "Thai sentence ending particles",
    ],
    tags: ["thai", "politeness", "beginner"],
    ctaLabel: "Learn Thai a sheet a day",
    ctaHref: "/",
    related: ["learning-thai-first-30-days", "first-100-thai-words"],
    author: null,
    noindex: false,
    publicationStatus: "preview",
    review: REVIEW,
    body: [
      {
        kind: "paragraph",
        text: "Thai politeness particles are the first thing a learner is taught and the first thing they drop under pressure. They sit at the very end of a sentence, they carry no meaning of their own, and leaving one off turns a polite request into a bare instruction without changing a single other word.",
      },
      { kind: "heading", text: "Which particle do you use, and when?" },
      {
        kind: "paragraph",
        text: "The particle follows the speaker, not the listener. A man ends a polite sentence with ครับ whether he is speaking to a man, a woman, a child or a room. A woman ends a polite statement with ค่ะ in the same situations. There is no version that changes because of who you are addressing, which makes it simpler than most learners expect.",
      },
      {
        kind: "table",
        caption: "The particle by speaker and sentence type",
        columns: ["Speaker", "Statement or request", "Question"],
        rows: [
          ["A man", "ครับ (khrap)", "ครับ (khrap)"],
          ["A woman", "ค่ะ (kha, falling tone)", "คะ (kha, high tone)"],
        ],
      },
      {
        kind: "paragraph",
        text: "The one wrinkle in that table is worth learning properly. A woman's particle changes tone in a question: ค่ะ has a falling tone in a statement, and คะ has a high tone at the end of a question. A man's ครับ does not change. Getting the tone wrong is not a disaster, but getting it right is one of the fastest ways to sound like someone who has actually listened to Thai.",
      },
      { kind: "heading", text: "What do the particles actually mean?" },
      {
        kind: "paragraph",
        text: "The particles do not translate. There is no English word they correspond to, which is why a dictionary entry for ครับ tends to say something unhelpful like polite ending. The closest honest description is that they mark the whole sentence as respectful, in the way that tone of voice does in English.",
      },
      {
        kind: "paragraph",
        text: "That untranslatability has a practical consequence for anyone learning from a two-column sheet. A politeness particle cannot sit opposite a Dutch or English equivalent and be quizzed, because there is nothing to hide behind the fold. Particles are learned in the sentences that use them, not as vocabulary rows, and a well-built sheet teaches them that way.",
      },
      { kind: "heading", text: "Where in the sentence does the particle go?" },
      {
        kind: "paragraph",
        text: "The particle goes last, after everything else, including a question word. Thai builds the sentence, marks the question if there is one, and then adds politeness on the end. In practice this means you can decide to be polite after you have finished thinking about what you wanted to say, which is convenient for a beginner assembling a sentence slowly.",
      },
      {
        kind: "list",
        items: [
          "สวัสดีครับ is a man saying hello politely.",
          "สวัสดีค่ะ is a woman saying hello politely.",
          "ขอบคุณครับ is a man saying thank you.",
          "ขอบคุณค่ะ is a woman saying thank you.",
        ],
      },
      {
        kind: "callout",
        title: "Use the particle that matches you",
        text: "Learners sometimes copy whichever particle they heard last, which means men repeating ค่ะ back to a shopkeeper. Pick the one that matches you and attach it to every polite sentence until it is automatic.",
      },
      { kind: "heading", text: "Can you ever leave the particle off?" },
      {
        kind: "paragraph",
        text: "Thai speakers leave particles off constantly, among close friends, within families, and in casual speech. A learner should not copy that early. Dropping a particle among friends reads as familiarity; dropping it as a stranger in a shop reads as brusqueness, and a beginner does not yet have the other signals that would make the difference obvious.",
      },
      {
        kind: "paragraph",
        text: "The safe rule for a first year is simple: use the particle with anyone you do not know, anyone older, and anyone serving you. Drop it only where you have heard the people around you drop it first.",
      },
      { kind: "heading", text: "What about the question particle ไหม?" },
      {
        kind: "paragraph",
        text: "ไหม is a different thing that often gets taught alongside the politeness particles, and confusing the two is common. ไหม turns a statement into a yes-or-no question, so it is grammar rather than politeness, and it comes before the politeness particle rather than instead of it. A polite yes-or-no question from a woman therefore ends with both: the question marker, then คะ.",
      },
      {
        kind: "paragraph",
        text: "Like the politeness particles, ไหม has no standalone translation and is best met inside whole sentences. If you are working through a first month of Thai, [what the first 30 days should cover](/articles/learning-thai-first-30-days) puts both of these where they belong: early, and always in context.",
      },
    ],
    faqs: [
      {
        question: "What is the difference between khrap and kha in Thai?",
        answer:
          "They are the same politeness marker for different speakers. A man ends a polite sentence with khrap and a woman with kha, no matter who they are speaking to. The particle follows the speaker, not the listener, so you use the one that matches you.",
      },
      {
        question: "Why does kha sometimes sound different in a question?",
        answer:
          "Because it changes tone. A woman uses a falling tone on kha in a statement and a high tone on kha at the end of a question. It is the same particle doing the same politeness job, with the tone marking the difference. The male particle khrap does not change this way.",
      },
      {
        question: "Do I have to use a politeness particle every time?",
        answer:
          "Use one with anyone you do not know, anyone older, and anyone serving you. Thai speakers drop particles freely among friends and family, but a beginner who drops them with strangers sounds blunt rather than casual, so keep them until you hear the people around you dropping them first.",
      },
      {
        question: "Can a man use kha or a woman use khrap?",
        answer:
          "Not in ordinary speech. The particles are tied to the speaker, so a man using kha sounds mistaken rather than polite. Learners often slip into this by repeating whichever particle they just heard, which is worth watching for in your first weeks.",
      },
      {
        question: "Is mai a politeness particle too?",
        answer:
          "No. Mai turns a statement into a yes or no question, so it is grammar rather than politeness. It comes before the politeness particle rather than replacing it, which means a polite question from a woman can end with both the question marker and kha.",
      },
    ],
    sources: [SOURCES.omniglotThai, SOURCES.unicodeThai, SOURCES.languageDeclarations],
  },

  {
    slug: "learning-a-language-together",
    title: "Learning a language together: one sheet, two people",
    description:
      "Two people can share one printed sheet if each folds away a different half. How to practise daily as a pair without one of you carrying the other.",
    lede: "Two people learning opposite directions of the same language pair can share one sheet, because a two-column page folded one way tests one of you and folded the other way tests the other. Learning together fails for a predictable reason: the faster learner turns into a teacher, the slower one turns into a student, and the student stops enjoying it.",
    emoji: "👥",
    category: "Together",
    date: "2026-08-16",
    updated: "2026-08-16",
    primaryKeyword: "learn a language together",
    secondaryKeywords: [
      "learning a language with your partner",
      "language practice for couples",
      "learn a language as a family",
      "practise a language with someone",
    ],
    tags: ["together", "method", "recall"],
    ctaLabel: "Put both names on a sheet",
    ctaHref: "/",
    related: ["the-fold-and-test-method", "how-many-words-a-day-should-you-learn"],
    author: null,
    noindex: false,
    publicationStatus: "preview",
    review: REVIEW,
    body: [
      {
        kind: "paragraph",
        text: "Learning a language with a partner or a housemate is the most reliable way to keep a daily habit and the most reliable way to ruin an evening. Both happen for the same reason: the other person is right there, which makes skipping harder and makes being worse than them more visible.",
      },
      { kind: "heading", text: "How do two people share one sheet?" },
      {
        kind: "paragraph",
        text: "Two people share one sheet by folding it in opposite directions. A page with Thai on the left and Dutch on the right hides the Thai when folded one way and the Dutch when folded the other, so the Dutch speaker learning Thai and the Thai speaker learning Dutch are working from exactly the same rows. Neither of you needs a second page, and neither of you gets an easier one.",
      },
      {
        kind: "list",
        ordered: true,
        items: [
          "Print one sheet with both names above the columns, so each of you knows which half is yours to hide.",
          "Fold away your own target language and produce it from the language you already speak.",
          "Swap: the other person folds the other way and does the same rows.",
          "Check each other only on the rows that were guesses, and stop there.",
        ],
      },
      {
        kind: "paragraph",
        text: "The names above the columns do more work than they look like they do. Without them, a shared sheet quietly becomes one person's sheet that the other is helping with, and the fold stops being symmetrical. [The fold-and-test method](/articles/the-fold-and-test-method) covers the mechanics of the fold itself.",
      },
      { kind: "heading", text: "Why does learning together usually fail?" },
      {
        kind: "paragraph",
        text: "Learning together usually fails because the two people are not at the same level and the session does not admit it. One of them starts explaining, the other starts nodding, and within a fortnight the evening is a lesson rather than a practice. A lesson has a teacher, and the teacher is no longer learning anything.",
      },
      {
        kind: "table",
        caption: "The four ways a shared session goes wrong",
        columns: ["What happens", "Why it happens", "The fix"],
        rows: [
          [
            "One of you explains the whole time",
            "Different levels, no structure",
            "Both fold the same rows, and correct only after both have tried",
          ],
          [
            "One of you sets the pace",
            "One person owns the app or the account",
            "One shared sheet, one shared pace, decided together",
          ],
          [
            "It becomes a competition",
            "Scores are visible and comparable",
            "Do not keep a score; the sheet is done or it is not",
          ],
          [
            "It stops when one of you is away",
            "The habit is attached to the pair",
            "Each of you can do the sheet alone; together is a bonus",
          ],
        ],
      },
      {
        kind: "callout",
        title: "Correct after, not during",
        text: "Interrupting a partner mid-attempt takes away the retrieval, which is the part that was doing the work. Let them finish the row wrong, then say the right answer once.",
      },
      { kind: "heading", text: "What if you are at very different levels?" },
      {
        kind: "paragraph",
        text: "Different levels are fine if the harder job goes to the stronger learner. On a shared sheet the two directions are not equally hard: producing the new language is harder than recognising it, so the person further ahead should be the one producing while the other recognises. That keeps both people uncomfortable enough to be learning without either being humiliated.",
      },
      {
        kind: "paragraph",
        text: "If the gap is very large, decouple the pace instead of splitting up. Each of you can run your own daily word count on your own sheets and still sit down at the same time, which keeps the ritual, which was the only part that depended on the other person anyway.",
      },
      { kind: "heading", text: "How does this work with children?" },
      {
        kind: "paragraph",
        text: "With children, the fold matters more than the content. A child who is asked to recall five words and gets four is playing a game they can win; a child who is asked to study is doing homework. Keep the sheet short, let them hold it, and let them test the adult as often as the adult tests them, because being the one holding the answers is most of the appeal.",
      },
      {
        kind: "paragraph",
        text: "A short sheet is also the honest choice for a household with an inconsistent evening. Five words that get done is worth more than twelve that get postponed, which is the same arithmetic [choosing a daily word count](/articles/how-many-words-a-day-should-you-learn) works through for one person.",
      },
    ],
    faqs: [
      {
        question: "How can two people learn a language from the same sheet?",
        answer:
          "By folding it in opposite directions. A two-column sheet hides one language when folded one way and the other language when folded the other way, so two people learning opposite directions of the same pair can work from identical rows without either of them needing a separate page.",
      },
      {
        question: "Why does learning a language with a partner usually stop?",
        answer:
          "Because the session turns into a lesson. One person ends up explaining and the other ends up nodding, so only one of them is still practising. Keeping both people producing answers before anyone corrects anything is what stops a shared practice becoming a class.",
      },
      {
        question: "What if my partner is much further ahead than me?",
        answer:
          "Give the harder direction to the stronger learner. Producing the new language is harder than recognising it, so the person further ahead should be producing while the other recognises. If the gap is very large, keep the shared time and run different daily word counts.",
      },
      {
        question: "Is learning a language together good for children?",
        answer:
          "Yes, if the sheet is short and the child gets to hold it. A child recalling five words is playing a game they can win, and letting them test the adult as often as the adult tests them is most of the appeal. Keep it to a few minutes and stop while it is still fun.",
      },
    ],
    sources: [SOURCES.retrievalPractice, SOURCES.cefr],
  },

  {
    slug: "first-100-thai-words",
    title: "The first 100 Thai words, and how far they really get you",
    description:
      "The 100 most common Thai words cover a large share of everyday speech, and far less of its meaning. Here is what they buy, and the order to meet them in.",
    lede: "A hundred well-chosen Thai words will carry you through greetings, prices, food, directions and time, which is most of what a visitor says out loud. What the familiar claim about 100 words covering half of conversation actually measures is word count, not understanding: the commonest words in any language are the small grammatical ones, and knowing them all still leaves you unable to name a single dish.",
    emoji: "💯",
    category: "Thai",
    date: "2026-08-16",
    updated: "2026-08-16",
    primaryKeyword: "100 basic thai words",
    secondaryKeywords: [
      "most common thai words",
      "basic thai vocabulary list",
      "essential thai words for travel",
      "thai words with english meaning",
    ],
    tags: ["thai", "vocabulary", "beginner"],
    ctaLabel: "Start on day 1",
    ctaHref: "/",
    related: [
      "learning-thai-first-30-days",
      "how-long-does-it-take-to-learn-thai",
      "how-many-words-a-day-should-you-learn",
    ],
    author: null,
    noindex: false,
    publicationStatus: "preview",
    review: REVIEW,
    body: [
      {
        kind: "paragraph",
        text: "A hundred basic Thai words is about twenty days of work, grouped the way a day happens rather than the way a dictionary is ordered. This page is the honest version of what that hundred buys, which topics it covers and in what order, and day 1 written out in full so you can see exactly what a sheet looks like. The remaining days are the course itself: five words a morning, two columns, one fold. There is no shortcut in which you absorb the hundred over a weekend, and a page that hands you all of it at once is selling that shortcut.",
      },
      { kind: "heading", text: "Do 100 words really cover half of everyday conversation?" },
      {
        kind: "paragraph",
        text: "The claim is true about word counts and misleading about conversations. In every language studied, a small set of grammatical words accounts for a very large share of all words spoken or written: the equivalents of the, and, is, not, I, you. Learn the hundred commonest words in a language and you will indeed recognise something close to half of the individual words in a sentence.",
      },
      {
        kind: "paragraph",
        text: "Recognising half the words is not understanding half the sentence. The words carrying the actual message, the nouns and verbs, sit in the half you do not have. This is why a frequency list is a poor curriculum on its own, and why the list below is grouped by situation instead: fifteen words about food are worth more on a Tuesday than the fifteen commonest particles in the language.",
      },
      {
        kind: "table",
        caption: "What a hundred words does and does not buy",
        columns: ["What you can do", "With 100 words", "What is still missing"],
        rows: [
          ["Open and close a conversation politely", "Yes", "Nothing; this is where 100 words are strongest"],
          ["Order food and ask the price", "Yes", "The names of most dishes"],
          ["Ask for and follow a direction", "Mostly", "Street and place names, and long answers"],
          ["Say when something is happening", "Yes, roughly", "Precise times and dates"],
          ["Understand a fast reply", "Rarely", "Everything the reply is actually about"],
          ["Explain a problem", "No", "Almost all of it"],
        ],
      },
      { kind: "heading", text: "Which 100 Thai words should you learn first?" },
      {
        kind: "paragraph",
        text: "Start with the words that open and close an exchange, because they are the ones you will use in every single conversation regardless of what it is about. A learner who can only greet, thank, apologise and say yes or no is already having short successful interactions, which is what keeps anyone going.",
      },
      {
        kind: "paragraph",
        text: "Here is day 1 in full, exactly as the sheet prints it: six words, Thai on one side and your own language on the other, with a fold line down the middle so either column can be hidden.",
      },
      {
        kind: "table",
        caption: "Day 1, in full",
        columns: ["Thai", "Say it as", "English"],
        rows: [
          ["สวัสดี", "sà-wàt-dee", "hello, goodbye"],
          ["ขอบคุณ", "khàwp-khun", "thank you"],
          ["สบายดีไหม", "sà-baai-dee mǎi", "how are you?"],
          ["สบายดี", "sà-baai-dee", "I am fine"],
          ["ยินดีที่ได้รู้จัก", "yin-dee thêe dâai rúu-jàk", "nice to meet you"],
          ["ไม่เป็นไร", "mâi bpen rai", "you are welcome, never mind"],
        ],
      },
      {
        kind: "paragraph",
        text: "The rest of the hundred arrives the same way, one sheet a morning, and that is a teaching decision rather than a tease. A hundred words printed on a single page is a page nobody works through: you read it, recognise most of it, feel like you have studied, and can produce almost none of it a week later. The same hundred spread across twenty mornings is twenty small tests you can actually pass or fail, which is the only part that moves anything into memory.",
      },
      {
        kind: "table",
        caption: "The first hundred words, in the order they arrive",
        columns: ["Days", "Topics", "What you can do by the end of it"],
        rows: [
          [
            "1 to 5",
            "Greetings, everyday phrases, people, family, questions",
            "Open and close an exchange, say who you are, ask something",
          ],
          [
            "6 to 10",
            "Numbers, colours, days of the week, time, the home",
            "Prices, dates, and arranging when",
          ],
          [
            "11 to 15",
            "Food, drinks, the restaurant, common verbs, adjectives",
            "The exchange a visitor has most often",
          ],
          [
            "16 to 20",
            "Feelings, clothes, body and health, animals, nature",
            "Say how you are and describe what is around you",
          ],
        ],
      },
      {
        kind: "paragraph",
        text: "At five words a day the hundredth word lands on day 20, and the starter course carries on to 151 words on day 31. Raise the daily count and the same topics arrive sooner: the order never changes, only how much of it one sheet holds. A day whose window crosses a topic boundary pulls from both and names both at the top of the page.",
      },
      {
        kind: "paragraph",
        text: "Two things in that first week are grammar rather than vocabulary, and both are worth learning properly instead of memorising as words. ไหม turns a statement into a yes or no question, and the polite endings ครับ and ค่ะ change with who is speaking rather than with the sentence. [Thai politeness particles](/articles/thai-politeness-particles-khrap-and-kha) covers the endings on their own, because getting them wrong is the mistake a Thai listener notices first.",
      },
      { kind: "heading", text: "How should you actually learn a hundred words?" },
      {
        kind: "paragraph",
        text: "Not as a list, which is the one thing every hundred-word page invites you to do. Reading a hundred words in one sitting produces a strong feeling of familiarity and almost no ability to produce any of them, and that feeling is the oldest trap in vocabulary study: recognition is not recall, and only recall gets you through a conversation. Take a handful a day, hide one column, and say the answer out loud before you check it.",
      },
      {
        kind: "callout",
        title: "A hundred words is twenty days, not one weekend",
        text: "At five a day the first hundred takes about three weeks, and you will still have all of it in a month. At a hundred in one weekend you will have perhaps fifteen by Wednesday.",
      },
      {
        kind: "paragraph",
        text: "Two other things matter more than which hundred words you start with. Say every word aloud from the first day, because Thai is tonal and a word you have only read silently is a word you cannot yet use. And test yourself in the harder direction, English to Thai, at least half the time, because that is the direction speaking runs in. [The fold-and-test method](/articles/the-fold-and-test-method) is the whole technique in five minutes a day.",
      },
      { kind: "heading", text: "What should the next hundred words be?" },
      {
        kind: "paragraph",
        text: "The second hundred should come from your own life rather than from any list. Whatever you found yourself unable to say in the first month is the correct second hundred: the food you actually order, the place you actually go, the thing you actually do for work. A generic second hundred is where most learners quietly stop, because it stops being about anything.",
      },
      {
        kind: "list",
        items: [
          "Keep a note on your phone of every word you wanted and did not have.",
          "Add the ten most repeated ones to your own list each week.",
          "Learn the script in month two, so a menu stops being a wall of shapes.",
          "Keep the daily count where it was; the list gets longer, the day does not.",
        ],
      },
    ],
    faqs: [
      {
        question: "How many Thai words do I need to be understood?",
        answer:
          "Around one hundred well-chosen words is enough for the short exchanges a visitor has: greeting someone, ordering, asking a price, and following a direction. Being understood in a longer conversation needs several hundred more, mostly nouns and verbs from your own life rather than from a frequency list.",
      },
      {
        question: "Do the 100 most common words really cover half of conversation?",
        answer:
          "They cover close to half the individual words, and much less than half the meaning. The commonest words in any language are small grammatical ones, so knowing all of them still leaves out the nouns and verbs a sentence is actually about. It is a real statistic that is usually quoted as if it meant something else.",
      },
      {
        question: "How long does it take to learn 100 Thai words?",
        answer:
          "About three weeks at five words a day, which is the pace most people can keep without a struggle. Learning them faster is easy and keeping them is not: a hundred words met in one weekend is typically down to a handful by the following Wednesday.",
      },
      {
        question: "Should I learn Thai words with the Thai script or with romanisation?",
        answer:
          "Use romanisation for the first month so you can say things straight away, then start the script in month two. Romanisation cannot show tone reliably and no two systems agree, so it is scaffolding rather than a destination. Learning the script after a hundred words means recognising words instead of decoding shapes.",
      },
      {
        question: "Which Thai words are most useful for travel?",
        answer:
          "The politeness endings, thank you, sorry, yes and no, the numbers one to ten plus hundred and thousand, and the words for water, rice, toilet, left, right and straight ahead. That handful covers ordering, paying, and getting somewhere, which is most of what a traveller says out loud.",
      },
    ],
    sources: [SOURCES.commonWords, SOURCES.omniglotThai, SOURCES.unicodeThai],
  },

  {
    slug: "how-long-does-it-take-to-learn-thai",
    title: "How long does it take to learn Thai?",
    description:
      "Weeks for useful phrases, months for short conversations, and years for fluency. Here is what each stage really takes, and which part of Thai is slowest.",
    lede: "You can be having short, useful exchanges in Thai within about a month of daily practice, holding simple conversations after six months to a year, and reading comfortably well after that. Professional fluency is a multi-year project: the US Foreign Service Institute puts Thai in its hard-language group at roughly 1,100 classroom hours, around double what it allows for Dutch.",
    emoji: "⏳",
    category: "Thai",
    date: "2026-08-16",
    updated: "2026-08-16",
    primaryKeyword: "how long does it take to learn thai",
    secondaryKeywords: [
      "is thai hard to learn",
      "how long to learn thai fluently",
      "thai language difficulty",
      "time to learn thai for english speakers",
    ],
    tags: ["thai", "beginner", "pace"],
    ctaLabel: "Start the first month",
    ctaHref: "/",
    related: [
      "learning-thai-first-30-days",
      "first-100-thai-words",
      "how-many-words-a-day-should-you-learn",
    ],
    author: null,
    noindex: false,
    publicationStatus: "preview",
    review: REVIEW,
    body: [
      {
        kind: "paragraph",
        text: "Every honest answer to this question is a range, because the question hides four different goals inside one word. Being understood in a shop, holding a conversation, reading a menu, and working in Thai are separated by years, and most people asking mean the first two.",
      },
      { kind: "heading", text: "How long until you can say something useful?" },
      {
        kind: "paragraph",
        text: "About a month of five or ten minutes a day gets you to useful. A hundred and fifty words covers greetings, numbers, food, directions and time, which is enough to open an exchange, order, pay and get somewhere. Nothing about that month is hard; it is a matter of turning up on more days than you skip.",
      },
      {
        kind: "table",
        caption: "What each stage takes at a steady daily pace",
        columns: ["Goal", "Roughly how long", "What decides it"],
        rows: [
          ["Greet, order, pay, ask directions", "About a month", "Showing up daily, not talent"],
          ["Short conversations on familiar topics", "Six months to a year", "How much you speak, not how much you study"],
          ["Reading menus and signs", "Two to three months for the script itself", "Whether you start the script at all"],
          ["Following fast speech between locals", "One to three years", "Listening hours, and only listening hours"],
          ["Working or studying in Thai", "Several years", "Roughly 1,100 hours of structured work, by FSI's estimate"],
        ],
      },
      {
        kind: "paragraph",
        text: "The Foreign Service Institute figure in the last row is worth reading carefully. It describes full-time, taught, intensive study aimed at professional working proficiency, which is a much higher bar than most learners want. It is useful mainly as a comparison: Thai sits with Russian and Vietnamese, and takes roughly twice as long as Dutch or Spanish for an English speaker.",
      },
      { kind: "heading", text: "What makes Thai slow, and what makes it fast?" },
      {
        kind: "paragraph",
        text: "Thai grammar is fast. There is no verb conjugation, no plural marking, no articles, and no grammatical gender, so a sentence assembled out of dictionary forms is very often simply correct. Learners coming from German or French are usually surprised how little there is to memorise.",
      },
      {
        kind: "paragraph",
        text: "Thai sound is slow. Thai is tonal, so the same syllable at a different pitch is a different word, and a beginner who ignores tone is not speaking with an accent but saying something else. Several vowel and consonant distinctions do not exist in English either, and none of them can be learned by reading.",
      },
      {
        kind: "table",
        caption: "Where the time actually goes",
        columns: ["Part of the language", "Share of the effort", "Why"],
        rows: [
          ["Grammar", "Small", "Very little inflection to learn"],
          ["Vocabulary", "Steady and unavoidable", "Few words resemble anything you already know"],
          ["Tone and pronunciation", "Large, and front-loaded", "It has to be trained aloud, from the first week"],
          ["Reading and writing", "Large, but optional at first", "A separate script, written without spaces between words"],
          ["Listening", "The largest of all", "Only hours of exposure move it"],
        ],
      },
      { kind: "heading", text: "Does studying more each day make it faster?" },
      {
        kind: "paragraph",
        text: "Studying more each day makes it faster only up to the point where you stop doing it. The arithmetic is unforgiving: ten minutes a day for a year is about sixty hours, while an ambitious hour a day abandoned in week three is about twenty. Consistency beats intensity here not as a slogan but as multiplication.",
      },
      {
        kind: "paragraph",
        text: "The one thing that genuinely accelerates Thai is speaking early and often, including badly. Tone is a motor skill, and motor skills do not improve from reading about them. [How many words a day to learn](/articles/how-many-words-a-day-should-you-learn) works through choosing a pace you will actually keep.",
      },
      {
        kind: "callout",
        title: "Two hundred hours, spent differently",
        text: "Two hundred hours of reading vocabulary lists produces a learner who recognises a lot and says nothing. The same two hundred hours split between recall practice and speaking produces someone who can hold a conversation badly, which is the only route to holding one well.",
      },
      { kind: "heading", text: "How long does the Thai script take?" },
      {
        kind: "paragraph",
        text: "The Thai script takes most learners two to three months of regular practice to read slowly, and much longer to read fluently. It is an abugida with forty-four consonants, a set of vowel signs that can sit before, after, above or below the consonant, and no spaces between words. None of that is hard in isolation; all of it together takes time.",
      },
      {
        kind: "paragraph",
        text: "Starting the script in month two rather than month one is the usual advice, and it holds up. Learning to read words you already say is far quicker than learning to decode shapes that mean nothing to you yet. [What the first 30 days should cover](/articles/learning-thai-first-30-days) sets out that order.",
      },
      { kind: "heading", text: "What should you expect after one year?" },
      {
        kind: "paragraph",
        text: "After a year of genuine daily practice, expect to handle everyday situations comfortably, understand a patient speaker talking about familiar things, read slowly, and lose the thread whenever two locals talk to each other at normal speed. That is a real and useful level, and it is roughly the CEFR A2 to B1 band the Council of Europe describes.",
      },
      {
        kind: "list",
        items: [
          "You will understand far more than you can say, and that gap is normal.",
          "Your tones will be the last thing to come right, and the first thing a listener notices.",
          "Listening will feel stuck for months and then move suddenly.",
          "The people you talk to matter more than the material you study.",
        ],
      },
    ],
    faqs: [
      {
        question: "Is Thai hard to learn for English speakers?",
        answer:
          "Thai grammar is easier than most European languages, with no conjugation, plurals or articles. The difficulty is in the sounds and the script: Thai is tonal, and it is written without spaces between words. Expect the grammar to feel quick and the pronunciation to take much longer.",
      },
      {
        question: "How long does it take to become fluent in Thai?",
        answer:
          "Several years for genuine fluency. The US Foreign Service Institute estimates around 1,100 hours of intensive taught study for professional working proficiency, roughly double what it allows for Dutch or Spanish. Conversational comfort on familiar topics arrives much sooner, usually within six months to a year of daily practice.",
      },
      {
        question: "Can I learn Thai in three months?",
        answer:
          "You can reach a genuinely useful level in three months of daily practice: greetings, food, prices, directions, time, and short exchanges on familiar topics. You will not be conversational in the sense of following two locals talking to each other, because that depends on listening hours nobody can compress.",
      },
      {
        question: "How long does the Thai alphabet take to learn?",
        answer:
          "Two to three months of regular practice to read slowly, and longer to read at speed. Most learners do better starting the script in month two, once they can already say a hundred or so words, because reading words you recognise is far faster than decoding unfamiliar shapes.",
      },
      {
        question: "Is it faster to learn Thai in Thailand?",
        answer:
          "Living in Thailand helps enormously with listening and with motivation, and not at all by itself. Plenty of long-term residents speak very little Thai, because the hours that count are the ones spent speaking and studying rather than the ones spent nearby.",
      },
    ],
    sources: [SOURCES.fsiTraining, SOURCES.cefr, SOURCES.omniglotThai],
  },

  {
    slug: "learning-english-as-a-thai-speaker",
    title: "Learning English as a Thai speaker: what to fix first",
    description:
      "Thai speakers hit the same handful of English problems: final consonants, r and l, tenses, and plurals. Here is what to fix first, and what to ignore.",
    lede: "For a Thai speaker, most of the distance to clear English is in four places: pronouncing the ends of words, telling r and l apart, marking tense on the verb, and marking plurals on the noun. None of these is about vocabulary, which is why a learner with a large vocabulary can still be hard to follow, and why fixing four habits changes more than another thousand words would.",
    emoji: "🔤",
    category: "English",
    date: "2026-08-16",
    updated: "2026-08-16",
    primaryKeyword: "learn english for thai speakers",
    secondaryKeywords: [
      "english pronunciation for thai speakers",
      "common english mistakes thai speakers",
      "thai to english learning",
      "english grammar for thai learners",
    ],
    tags: ["english", "pronunciation", "beginner"],
    ctaLabel: "Practise English daily",
    ctaHref: "/",
    related: [
      "the-fold-and-test-method",
      "how-many-words-a-day-should-you-learn",
      "learning-dutch-as-a-thai-speaker",
    ],
    author: null,
    noindex: false,
    publicationStatus: "preview",
    review: REVIEW,
    body: [
      {
        kind: "paragraph",
        text: "Thai and English are unrelated languages with almost no shared vocabulary, and yet the errors Thai speakers make in English are remarkably consistent. That consistency is good news: a predictable problem can be worked on directly, rather than waited out.",
      },
      { kind: "heading", text: "Why do the ends of English words disappear?" },
      {
        kind: "paragraph",
        text: "Thai allows only a small set of sounds at the end of a syllable, and it releases none of them the way English does. English is full of endings Thai does not have, so the mouth does what it has always done and drops or softens them. The result is that _rice_, _right_ and _ride_ come out close enough to be the same word.",
      },
      {
        kind: "paragraph",
        text: "This one habit costs more clarity than any other, because English leans on final consonants to carry grammar. A missing final s removes a plural, a missing final d removes the past tense, and the listener loses the meaning rather than just the sound.",
      },
      {
        kind: "table",
        caption: "The endings worth practising first",
        columns: ["Ending", "What it does in English", "What it becomes without practice"],
        rows: [
          ["final s and es", "plurals, and he or she does something", "cat for cats, work for works"],
          ["final ed", "the past tense", "walk for walked"],
          ["final l", "hotel, people, small", "a vowel, or nothing at all"],
          ["final s in a cluster", "asks, tests, months", "the cluster collapses to one sound"],
          ["final v and th", "have, with, breathe", "f, or t, or nothing"],
        ],
      },
      { kind: "heading", text: "How do you separate r and l?" },
      {
        kind: "paragraph",
        text: "Thai has both sounds, but they interchange freely in everyday speech and ร is often pronounced as l anyway, so the distinction never became load-bearing. In English it is: _rice_ and _lice_, _right_ and _light_, _collect_ and _correct_ are different words, and no context rescues them.",
      },
      {
        kind: "paragraph",
        text: "Practise them as pairs, out loud, in a mirror, and slowly. The l is made with the tongue tip firmly against the ridge behind the top teeth; the English r never touches anything. Say _light, right, light, right_ until your tongue notices it is doing two different things, then put both in one sentence.",
      },
      { kind: "heading", text: "Which grammar actually matters?" },
      {
        kind: "paragraph",
        text: "Thai marks time with words like already and tomorrow rather than by changing the verb, and it does not mark plurals on the noun at all. English does both, and a listener who does not hear them has to reconstruct your meaning from context. These two habits are worth more attention than any other grammar point.",
      },
      {
        kind: "table",
        caption: "The four habits that change the most",
        columns: ["Habit", "Thai equivalent", "What to practise"],
        rows: [
          ["Past tense on the verb", "Marked with a time word instead", "Say the ed ending out loud, every time"],
          ["Plural s on the noun", "No plural marking", "Two books, three cars, ten minutes"],
          ["Articles a, an, the", "No articles at all", "Notice them; do not panic about them"],
          ["Verb to be", "Often absent in Thai sentences", "She is tired, they are here"],
        ],
      },
      {
        kind: "paragraph",
        text: "Articles deserve a note. English articles are genuinely hard for a speaker of a language without them, and they are also the error that costs the least. A sentence missing _the_ is understood; a sentence missing tense and plural is not. Fix the load-bearing ones first and let articles come slowly.",
      },
      {
        kind: "callout",
        title: "Tone is not the problem, stress is",
        text: "Thai speakers often expect English pitch to matter the way tone does. It does not. What English uses instead is stress: one syllable in a word, and one word in a sentence, is longer and louder. Getting stress wrong is what makes a correct sentence hard to follow.",
      },
      { kind: "heading", text: "How should you practise, day to day?" },
      {
        kind: "paragraph",
        text: "Practise in both directions and out loud. Reading English is easy to overrate, because recognition grows fast and production does not; a learner who reads well and never speaks stays hard to understand for years. Producing an English sentence from Thai, aloud, is the exercise that moves the needle.",
      },
      {
        kind: "list",
        ordered: true,
        items: [
          "Take five words or phrases a day, with Thai on one side and English on the other.",
          "Cover the English and produce it from the Thai, saying every ending.",
          "Cover the Thai and do it the other way, which is easier and worth doing second.",
          "Record yourself once a week and listen for the endings you are dropping.",
        ],
      },
      {
        kind: "paragraph",
        text: "A two-column sheet folded down the middle is exactly this exercise on paper, which is what [the fold-and-test method](/articles/the-fold-and-test-method) describes. Five a day is enough; [how many words a day to learn](/articles/how-many-words-a-day-should-you-learn) explains why raising it usually backfires.",
      },
      { kind: "heading", text: "What can you safely ignore for now?" },
      {
        kind: "paragraph",
        text: "Ignore your accent, ignore idioms, and ignore the difference between British and American spelling. An accent is not an error and never stops anyone being understood. Idioms are the reward for a level you have not reached yet, and spending a month on them buys nothing a listener will notice.",
      },
      {
        kind: "paragraph",
        text: "What cannot be ignored is speaking to actual people. Thai speakers often delay this until their English feels ready, which is a level that never arrives on its own, because the thing that makes it ready is the speaking.",
      },
    ],
    faqs: [
      {
        question: "Why is English pronunciation hard for Thai speakers?",
        answer:
          "Mostly because of the ends of words. Thai allows only a few sounds at the end of a syllable and does not release them the way English does, so English endings get dropped or softened. Since English uses those endings to mark plurals and past tense, dropping them removes meaning rather than just sound.",
      },
      {
        question: "How can a Thai speaker practise the difference between r and l?",
        answer:
          "Practise them as pairs, slowly and out loud: light and right, lice and rice, collect and correct. The l is made with the tongue tip pressed against the ridge behind the top teeth, while the English r touches nothing. Say the pairs until the two feel physically different, then use both in one sentence.",
      },
      {
        question: "Which English grammar should a Thai speaker learn first?",
        answer:
          "Past tense on the verb and plural s on the noun. Thai marks time with separate words and does not mark plurals at all, so both are new habits rather than new facts. Articles are genuinely difficult and matter far less, because a listener recovers a missing the and cannot recover a missing tense.",
      },
      {
        question: "Do I need to lose my Thai accent to speak good English?",
        answer:
          "No. An accent is not an error, and almost nobody loses one after childhood. What matters is that word endings are audible and that stress lands on the right syllable, because those carry meaning. A strong accent with clear endings is far easier to follow than a light accent without them.",
      },
      {
        question: "How long does it take a Thai speaker to learn English?",
        answer:
          "Everyday conversation usually takes six months to a couple of years of regular practice, depending mostly on how much speaking is involved rather than how much study. The four habits above are worth attacking early, because they change how well you are understood long before your vocabulary does.",
      },
    ],
    sources: [SOURCES.cefr, SOURCES.omniglotThai, SOURCES.speechSynthesis],
  },

  {
    slug: "learning-dutch-as-a-thai-speaker",
    title: "Learning Dutch as a Thai speaker: where to start",
    description:
      "Dutch asks a Thai speaker for three new habits: verb endings, two words for the, and word order that moves. Here is what to learn first and why.",
    lede: "For a Thai speaker, Dutch is hard in exactly the places Thai is simple: verbs change their ending, every noun carries either de or het, and the verb moves around the sentence. Dutch pronunciation is far kinder than its reputation suggests, and it is not tonal, so the sounds are a smaller problem than the grammar.",
    emoji: "🚲",
    category: "Dutch",
    date: "2026-08-16",
    updated: "2026-08-16",
    primaryKeyword: "learn dutch as a thai speaker",
    secondaryKeywords: [
      "dutch for beginners thai",
      "basic dutch words and phrases",
      "dutch grammar for beginners",
      "learn dutch daily practice",
    ],
    tags: ["dutch", "beginner", "vocabulary"],
    ctaLabel: "Start Thai to Dutch",
    ctaHref: "/",
    related: [
      "learning-english-as-a-thai-speaker",
      "learning-a-language-together",
      "how-many-words-a-day-should-you-learn",
    ],
    author: null,
    noindex: false,
    publicationStatus: "preview",
    review: REVIEW,
    body: [
      {
        kind: "paragraph",
        text: "Thai and Dutch have nothing in common: no shared vocabulary, no shared script, and opposite instincts about what a sentence should mark. That sounds discouraging and is mostly useful, because it means the difficulties are predictable and can be met head on.",
      },
      { kind: "heading", text: "What is genuinely hard about Dutch after Thai?" },
      {
        kind: "paragraph",
        text: "Three things carry almost all of the difficulty. Dutch verbs change their ending depending on who is doing the action, where Thai verbs never change. Dutch nouns take either de or het and there is no reliable rule for which, where Thai has no articles at all. And the Dutch verb moves: it sits second in a main clause and goes to the end in a subordinate one.",
      },
      {
        kind: "table",
        caption: "Where Thai habits do not transfer",
        columns: ["Dutch asks for", "Thai does", "What to practise"],
        rows: [
          ["Verb endings by person", "Nothing changes", "ik werk, jij werkt, hij werkt"],
          ["de or het before every noun", "No articles", "Learn the article with the noun, never after it"],
          ["Verb second in the sentence", "Fixed word order", "Vandaag ga ik, not Vandaag ik ga"],
          ["Plural endings", "No plural marking", "boek and boeken, huis and huizen"],
          ["Separable verbs", "No equivalent", "opstaan becomes ik sta op"],
        ],
      },
      {
        kind: "paragraph",
        text: "The de and het problem deserves the strongest advice in this article: learn the article as part of the word, from the first day. _het huis_ is a single item to memorise, not a noun plus a decision. A learner who stores nouns bare will be guessing for years, and roughly two thirds of the time the guess is de.",
      },
      { kind: "heading", text: "What is easier than you expect?" },
      {
        kind: "paragraph",
        text: "Dutch pronunciation is more forgiving than its reputation. It is not tonal, the stress patterns are regular, and the alphabet is one you can read on day one. The famous g, the sound in _goed_ and _acht_, is genuinely unfamiliar to a Thai speaker, and it is one sound rather than a system.",
      },
      {
        kind: "paragraph",
        text: "Dutch is also generous about being spoken badly. Word order errors and a wrong article rarely stop a Dutch speaker understanding you, which makes it a good language to start using early and imperfectly.",
      },
      {
        kind: "table",
        caption: "The first Dutch words worth having",
        columns: ["Dutch", "English", "Where it comes up"],
        rows: [
          ["hallo, dag", "hello, bye", "Every exchange"],
          ["alstublieft", "please, here you are", "Shops, and handing something over"],
          ["dank je wel", "thank you", "Every exchange"],
          ["ja, nee", "yes, no", "Every exchange"],
          ["hoeveel kost het", "how much does it cost", "Shops and markets"],
          ["waar is", "where is", "Directions"],
          ["ik wil graag", "I would like", "Ordering anything"],
          ["ik begrijp het niet", "I do not understand", "The most useful sentence of month one"],
          ["spreekt u Engels", "do you speak English", "The escape hatch"],
          ["het water, het brood", "water, bread", "Learned with het, never bare"],
          ["de winkel, de trein", "the shop, the train", "Learned with de, never bare"],
        ],
      },
      { kind: "heading", text: "How should a Thai speaker practise Dutch daily?" },
      {
        kind: "paragraph",
        text: "Practise Dutch against Thai rather than against English. Going through a third language doubles the work and installs an English accent in your Dutch, and it also means every recall attempt tests two links instead of one. A sheet with Thai on one side and Dutch on the other is the shortest path between the two languages you actually use.",
      },
      {
        kind: "list",
        ordered: true,
        items: [
          "Take five Dutch words a day, each stored with its article where it has one.",
          "Fold the sheet so the Dutch is hidden and produce it from the Thai, out loud.",
          "Fold the other way for recognition, which is easier and worth doing second.",
          "Add one whole sentence a day rather than five, because word order needs sentences.",
        ],
      },
      {
        kind: "paragraph",
        text: "If you live with a Dutch speaker, the same sheet works for both of you at once: they fold away the Thai and learn in the opposite direction. [Learning a language together](/articles/learning-a-language-together) covers how to run that without one of you turning into the teacher.",
      },
      {
        kind: "callout",
        title: "Sentences teach word order, words cannot",
        text: "Dutch word order is the one thing a vocabulary list will never give you. One sentence a day, said aloud and correctly, is worth more than ten extra nouns.",
      },
      { kind: "heading", text: "Does living in the Netherlands make it faster?" },
      {
        kind: "paragraph",
        text: "Living in the Netherlands helps far less than people expect, for one specific reason: Dutch speakers switch to English the moment they detect a struggle, and they do it out of politeness. Plenty of long-term residents never get past the first month of Dutch because every conversation ends in English before it starts.",
      },
      {
        kind: "paragraph",
        text: "The fix is to say early and plainly that you are learning and would like to continue in Dutch. Most people will happily slow down and stay in Dutch once asked; almost nobody offers it unprompted.",
      },
      {
        kind: "paragraph",
        text: "For what counts as each level, the Council of Europe's framework is the reference the Dutch integration exams are built around, and the Taalunie is the authority on the standard language itself.",
      },
    ],
    faqs: [
      {
        question: "Is Dutch hard to learn for Thai speakers?",
        answer:
          "The grammar is the hard part, because Dutch marks things Thai never marks: verb endings, plurals, and an article on every noun. The pronunciation is easier than its reputation, since Dutch is not tonal and its stress is regular. Expect the sounds to come quickly and the sentence structure to take much longer.",
      },
      {
        question: "Should a Thai speaker learn Dutch through English?",
        answer:
          "No, learn it against Thai directly. Going through English doubles the work, because every recall attempt tests two links instead of one, and it tends to install an English accent in your Dutch. A sheet with Thai on one side and Dutch on the other is the shorter path.",
      },
      {
        question: "How do I know whether a Dutch word takes de or het?",
        answer:
          "There is no reliable rule, which is why the article has to be learned as part of the word from the first day. Store het huis as one item rather than as a noun plus a decision. Roughly two thirds of Dutch nouns take de, so a guess is better than nothing and much worse than learning it properly.",
      },
      {
        question: "What are the most useful Dutch phrases to learn first?",
        answer:
          "Hallo, dank je wel, alstublieft, ja and nee, hoeveel kost het, and waar is. Add ik begrijp het niet, which is the single most useful sentence of a first month, because it keeps a conversation going in Dutch instead of ending it in English.",
      },
      {
        question: "Why do Dutch people switch to English when I try to speak Dutch?",
        answer:
          "Out of politeness, and it is the main reason living in the Netherlands helps less than people expect. Say early and plainly that you are learning and would like to continue in Dutch. Most people are glad to slow down once asked, and almost nobody offers it without being asked.",
      },
    ],
    sources: [SOURCES.dutchLanguageUnion, SOURCES.cefr, SOURCES.retrievalPractice],
  },
];
